import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import logo from '../logo.png';
import { resetPassword } from '../services/userService';

export default function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasToken = useMemo(() => Boolean(token), [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!hasToken) {
      setError('Reset link is missing or invalid.');
      return;
    }

    if (!password) {
      setError('New password is required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await resetPassword({ token, password });
      setSuccessMessage(result?.message || 'Password reset successful.');
      window.setTimeout(() => {
        navigate('/', { replace: true });
      }, 1800);
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', p: { xs: 3, sm: 4 } }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: 128,
              height: 128,
              borderRadius: '50%',
              bgcolor: 'common.white',
              p: 1,
            }}
          />

          {successMessage ? (
            <CheckCircleRoundedIcon color="success" sx={{ fontSize: 64 }} />
          ) : (
            <LockResetRoundedIcon color="primary" sx={{ fontSize: 64 }} />
          )}

          <Stack spacing={1}>
            <Typography variant="h5" component="h1" fontWeight={700}>
              Reset your password
            </Typography>
            <Typography color="text.secondary">
              Choose a new password to regain access to your account.
            </Typography>
          </Stack>

          {!hasToken && (
            <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
              This reset link is missing a token. Request a new password reset email.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && (
                <Alert severity="success" sx={{ textAlign: 'left' }}>
                  {successMessage} Redirecting to sign in.
                </Alert>
              )}
              <TextField
                label="New password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                disabled={!hasToken || Boolean(successMessage)}
              />
              <TextField
                label="Confirm new password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
                fullWidth
                disabled={!hasToken || Boolean(successMessage)}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!hasToken || isSubmitting || Boolean(successMessage)}
              >
                {isSubmitting ? 'Resetting...' : 'Reset password'}
              </Button>
            </Stack>
          </Box>

          <Button component={RouterLink} to="/" variant="text">
            Back to sign in
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
